import { Col, Row, Form } from "react-bootstrap";
import Button from "@mui/material/Button";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { Grid, Box, Typography, Checkbox, IconButton } from "@mui/material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getAccounts, getRoles } from "../../api/groupsApi/groupsApi";
import { useEffect, useState } from "react";
import {
  getFolderAccessRights,
  saveFolderAccessRightsPermission,
} from "../../api/accessRightApi/accessRightApi";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useLoader } from "../../context/LoaderContext";
import { useSnackbar } from "../Common/SnackbarContext";
import LocalStorageUtil from "../../utils/localStorage";

type PermissionType = "read" | "write" | "delete";

const AccessRights = () => {
  const { setLoading } = useLoader();
  const { showSnackbar } = useSnackbar();
  const [roles, setRoles] = useState([]);
  const [accountData, setAccountData] = useState<any[]>([]);
  const [allData, setAllData] = useState<any>(null);
  const [collapsedFolders, setCollapsedFolders] = useState<{
    [key: string]: boolean;
  }>({});
  const [formData, setFormData] = useState({
    roleId: "",
  });
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    handleRolesChange(value);
  };

  const getAllAccountsName = async () => {
    setLoading(true);
    const payload = {
      contextName: LocalStorageUtil.getItem("contextName"),
    };
    try {
      const data = await getAccounts(payload);
      setAccountData(data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const getAllRoles = async () => {
    const businessUnitId = LocalStorageUtil.getItem("businessUnitId");
    const contextName = LocalStorageUtil.getItem("contextName");
    const payload = {
      businessUnit: {
        accountId: 0,
        accountName: "MetLifeBrighthouse",
        active: false,
        logoPath: null,
        isDefault: false,
        accountCode: null,
        canExpandLevel: null,
        timezone: null,
        isDashBoard: false,
        folderRights: null,
        userId: 0,
        primaryGroupId: 0,
        isSuperAdmin: 0,
        businessUnitId: businessUnitId,
        contextName: contextName,
        canPrefixLevel1: false,
        isUserPreferences: false,
        psuedoName: null,
        apiKey: null,
        serviceName: null,
        $$hashKey: "object:42860",
      },
      contextName: contextName,
    };
    try {
      setLoading(true);
      const response = await getRoles(payload);
      setLoading(false);
      setRoles(response);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRolesChange = async (roleId: any) => {
    const payload = {
      AccountId: LocalStorageUtil.getItem("accountId"),
      roleId: roleId,
      contextName: LocalStorageUtil.getItem("contextName"),
    };
    try {
      setLoading(true);
      const response = await getFolderAccessRights(payload);
      setLoading(false);
      setAllData(response);
      const collapseState: { [key: string]: boolean } = {};
      const populateCollapseState = (folders: any[]) => {
        folders.forEach((folder) => {
          collapseState[folder.folderId] = true;
          if (folder.listAccess?.length) {
            populateCollapseState(folder.listAccess);
          }
        });
      };
      populateCollapseState(response);
      setCollapsedFolders(collapseState);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllRoles();
    getAllAccountsName();
  }, []);

  const toggleCollapse = (folderId: string) => {
    setCollapsedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const updateChildrenRecursively = (
    children: any[],
    type: PermissionType,
    newValue: boolean
  ): any[] => {
    return children.map((child) => ({
      ...child,
      [type]: newValue,
      listAccess: child.listAccess
        ? updateChildrenRecursively(child.listAccess, type, newValue)
        : [],
    }));
  };

  const updateTargetFolder = (
    folder: any,
    folderId: string,
    type: PermissionType
  ) => {
    if (folder.folderId !== folderId) {
      return folder;
    }

    const newValue = !folder[type];

    if (!folder.isParent) {
      return { ...folder, [type]: newValue };
    }

    return {
      ...folder,
      [type]: newValue,
      listAccess: folder.listAccess
        ? updateChildrenRecursively(folder.listAccess, type, newValue)
        : [],
    };
  };

  const updateParentFolder = (
    folder: any,
    updatedChildren: any[],
    type: PermissionType
  ) => {
    const allChildrenChecked = updatedChildren.every((child) => child[type]);
    return {
      ...folder,
      [type]: allChildrenChecked,
      listAccess: updatedChildren,
    };
  };

  const toggleCheckbox = (folderId: string, type: PermissionType) => {
    const updateFolders = (folders: any[]): any[] => {
      return folders.map((folder) => {
        const updatedFolder = updateTargetFolder(folder, folderId, type);
        if (updatedFolder !== folder) {
          return updatedFolder;
        }

        if (!folder.listAccess?.length) {
          return folder;
        }

        const updatedChildren = updateFolders(folder.listAccess);
        return updateParentFolder(folder, updatedChildren, type);
      });
    };
    setAllData((prevData: any) => updateFolders(prevData));
  };

  const renderFolders = (folders: any[], level = 0) => {
    return folders.map((folder, index) => {
      const isCollapsed = collapsedFolders[folder.folderId];
      return (
        <Box key={folder.folderId}>
          <Grid
            container
            alignItems="center"
            onClick={() => setSelectedFolderId(folder.folderId)}
            sx={{
              borderBottom: "1px solid var(--aggrid-text-color)",
              py: 1,
              backgroundColor:
                selectedFolderId === folder.folderId
                  ? "rgba(255, 255, 255, 0.2)"
                  : "var(--aggrid-primary-color)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                cursor: "pointer",
              },
            }}
          >
            <Grid item xs={6}>
              <Box
                sx={{ pl: level * 2, display: "flex", alignItems: "center" }}
              >
                {folder.listAccess?.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={() => toggleCollapse(folder.folderId)}
                    sx={{ color: "var(--aggrid-text-color)" }}
                  >
                    {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                  </IconButton>
                )}
                <Typography
                  className="customTypographyOverride"
                  sx={{
                    ml: 1,
                    color: "var(--aggrid-text-color)",
                    fontWeight: 600,
                  }}
                >
                  {folder.foldername}
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                pr: 2,
              }}
            >
              <Checkbox
                checked={folder.read}
                onChange={() => toggleCheckbox(folder.folderId, "read")}
                sx={{ color: "var(--aggrid-text-color)" }}
              />
              <Checkbox
                checked={folder.write}
                onChange={() => toggleCheckbox(folder.folderId, "write")}
                sx={{ color: "var(--aggrid-text-color)" }}
              />
              <Checkbox
                checked={folder.delete}
                onChange={() => toggleCheckbox(folder.folderId, "delete")}
                sx={{ color: "var(--aggrid-text-color)" }}
              />
            </Grid>
          </Grid>
          {!isCollapsed &&
            folder.listAccess?.length > 0 &&
            renderFolders(folder.listAccess, level + 1)}
        </Box>
      );
    });
  };

  const handleSave = async () => {
    const payload = {
      RoleId: formData.roleId,
      AccountId: LocalStorageUtil.getItem("accountId"),
      listFolderAccess: allData,
      UserId: LocalStorageUtil.getItem("userId"),
      contextName: LocalStorageUtil.getItem("contextName"),
    };
    try {
      setLoading(true);
      const response = await saveFolderAccessRightsPermission(payload);
      if (response === "Fails") {
        showSnackbar(
          "Please select atleast one folder to provide access",
          true
        );
      } else if (response === "Success") {
        showSnackbar("Folder rights have been successfully provided");
      } else if (response !== "Success" && response !== "Fails") {
        showSnackbar("Failed to Save Folder rights", true);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data", error);
      showSnackbar("Failed to Save Folder rights", true);
    } finally {
      setLoading(false);
    }
  };

  const matchedAccount = accountData.find(
    (acc: any) =>
      acc.businessUnitId === Number(LocalStorageUtil.getItem("businessUnitId"))
  );

  return (
    <div>
      <NavigationBar
        title="User Management"
        screenTitle="Access Rights"
        text="Access Rights"
        menuTitle="Configuration"
      />
      <div
        className="ag-theme-quartz-dark"
        style={{ marginTop: "20px", marginLeft: "20px" }}
      >
        <Form>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formCustomer">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Customer name
                </Form.Label>
                <Form.Select
                  name="accountName"
                  value={matchedAccount?.accountName || ""}
                  disabled
                  style={{ fontSize: "11px" }}
                >
                  <option value="">
                    {matchedAccount?.accountName || "Select customer"}
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="formRole">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Role name
                </Form.Label>
                <Form.Select
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleChange}
                  style={{ fontSize: "11px" }}
                >
                  <option value="">Select</option>
                  {roles.map((role: any) => (
                    <option key={role.roleId} value={role.roleId}>
                      {role.roleName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col
              md={6}
              style={{
                display: "flex",
                justifyContent: "end",
                marginTop: "20px",
              }}
            >
              <Button
                variant="contained"
                className="update-button"
                onClick={handleSave}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
        <Box
          sx={{
            backgroundColor: "rgb(77, 123, 167)",
            padding: "10px 30px",
            marginTop: "20px",
            borderRadius: "3px",
          }}
        >
          <Grid container>
            <Grid item xs={6}>
              <Typography
                className="customTypographyOverride"
                sx={{ color: "var(--aggrid-text-color)", pl: 2 }}
              >
                Folder Name
              </Typography>
            </Grid>
            <Grid
              item
              xs={6}
              sx={{ display: "flex", justifyContent: "flex-end", gap: 4 }}
            >
              <LibraryBooksIcon sx={{ color: "var(--aggrid-text-color)" }} />
              <EditIcon sx={{ color: "var(--aggrid-text-color)" }} />
              <DeleteIcon sx={{ color: "var(--aggrid-text-color)" }} />
            </Grid>
          </Grid>
        </Box>
        {allData?.length > 0 ? (
          renderFolders(allData)
        ) : (
          <Typography sx={{ mt: 2 }}>
            Please select a role to view access rights.
          </Typography>
        )}
      </div>
    </div>
  );
};

export default AccessRights;
